import React, { FunctionComponent, useEffect, useState } from 'react';
import {
    Button,
    Grid,
    makeStyles,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@material-ui/core';
import moment from 'moment';
import { v1 as uuid } from 'uuid';
import { Account, FeeTypeList, JournalEntry, LineItem } from '../../../store/modules/accounting/types';
import { ACCOUNTING_ACCOUNTS_RECEIVABLE, ACCOUNTING_UNCATEGORIZED_REVENUE } from '../../../store/app.constants';
import { displayCurrencyAmount } from '../../../utils/utils';
import EditIcon from '@material-ui/icons/Edit';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import DatePicker from '../../../components/DatePicker';
import Select from '@material-ui/core/Select';
import theme from 'src/assets/theme';
import { grey } from '@material-ui/core/colors';
import { Person } from 'src/store/modules/person/types';
import { License } from 'src/store/modules/application/types';
import { getPersonSimpleName } from '../../../utils/utils';
import InputTextComponent from 'src/components/InputTextField';
import classNames from 'classnames';
import { round } from 'lodash';

type InvoiceProps = {
    invoice: JournalEntry;
    addLineItems: Function;
    feeTypes: FeeTypeList;
    getFeeTypes: Function;
    person: Person;
    license?: License;
};

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(4),
        margin: theme.spacing(4),
    },
    invoiceDate: {
        textAlign: 'right',
    },
    invoiceeName: {
        fontWeight: 'bold',
    },
    label: {
        fontWeight: 'bold',
    },
    invoiceItems: {
        marginTop: theme.spacing(4),
    },
    invoiceSummary: {
        borderWidth: 0,
        fontSize: '0.875em',
    },
    invoiceBalance: {
        borderTopWidth: 1,
        borderTopStyle: 'solid',
        borderTopColor: theme.palette.grey[300],
        fontWeight: 'bold',
    },
    modalBtn: {
        marginLeft: 20,
        sx: { p: 1, backgroundColor: 'white' },
    },
    textField: {
        fontSize: 13,
        fontWeight: 450,
        lineHeight: '21px',
        wordWrap: 'break-word',
        width: '100%',
    },
    datePickerInput: {
        fontSize: 13,
        border: '1px solid rgb(0,0,0,0.23) !important',
        paddingTop: 7,
        paddingBottom: 3,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        borderRadius: 3,
        marginBottom: '5px',
        color: '#484848 !important',
        width: 150,
        '&:before': {
            borderBottomColor: 'white',
        },
        '&:after': {
            borderBottomColor: 'white',
        },
        '&:hover:before': {
            borderBottomColor: ['white', '!important'],
        },
    },
    startAdornment: {
        paddingLeft: '2px',
        paddingTop: '2px',
        paddingBottom: '2px',
    },
    textFieldInput: {
        fontSize: 13,
        border: '1px solid rgb(0,0,0,0.23) !important',
        padding: 10,
        borderRadius: 3,
        marginBottom: '5px',
        color: '#484848 !important',
    },
    amountInput: {
        textAlign: 'right',
    },
}));

const Invoice: FunctionComponent<InvoiceProps> = (props) => {
    function sanitizeAmount(amount: number) {
        return round(amount, 2);
    }

    const { invoice, addLineItems, feeTypes, getFeeTypes, person, license } = props;
    const classes = useStyles();

    // Only A/R items are part of the invoice.
    const invoiceItems = invoice.lineItems.filter((item) => item.account.id === ACCOUNTING_ACCOUNTS_RECEIVABLE);

    const [invoiceTotal, setInvoiceTotal] = useState(
        invoiceItems.reduce((sum, item) => sum + (item.type === 'debit' ? item.amount : -item.amount), 0),
    );

    const amountReceived = invoice.invoicing?.amountPaid ?? 0;
    const [invoiceBalance, setInvoiceBalance] = useState(invoiceTotal - amountReceived);

    const [isEditable, setIsEditable] = useState(true);
    const [lineItems, setLineItems] = useState(invoice.lineItems);
    const [errorMessage, setErrorMessage] = useState(false);
    const [amountErrorMessage, setAmountErrorMessage] = useState(false);
    const [lineItem, setLineItem] = useState({
        type: 'debit',
        date: '',
        item: invoiceItems[0].feeType?.name ?? '<none>',
        amount: '',
        description: '',
        action: 'Reduce',
    });
    const isUnpaid = invoice.invoicing?.status === 'Open';

    useEffect(() => {
        getFeeTypes();
    }, [getFeeTypes]);

    const handleEditIcon = () => {
        setIsEditable(false);
    };

    const handleCloseIcon = () => {
        setIsEditable(true);
        setErrorMessage(false);
    };

    const handleSaveIcon = () => {
        if (lineItem.item && lineItem.amount && lineItem.type && lineItem.description) {
            setErrorMessage(false);
            setAmountErrorMessage(false);
            const amount = parseFloat(lineItem.amount);
            const feeType = feeTypes.find((feeType) => feeType.name === lineItem.item);
            const revenueAccount: Account = feeType?.revenueAccount
                ? { ...feeType.revenueAccount, type: 'Revenue' }
                : {
                      id: ACCOUNTING_UNCATEGORIZED_REVENUE,
                      name: 'Uncategorized Revenue',
                      type: 'Revenue',
                  };

            const newFeeType = feeType
                ? {
                      id: feeType.id,
                      name: feeType.name,
                  }
                : undefined;

            const newLineItems: LineItem[] = [
                {
                    type: lineItem.type as 'debit' | 'credit',
                    amount,
                    description: lineItem.description,
                    serviceDate: lineItem.date || undefined,
                    account: revenueAccount,
                    feeType: newFeeType,
                    person: {
                        id: person.id,
                        name: getPersonSimpleName(person.name),
                    },
                },
                {
                    type: lineItem.type === 'debit' ? 'credit' : 'debit',
                    amount,
                    description: lineItem.description,
                    serviceDate: lineItem.date || undefined,
                    account: {
                        id: ACCOUNTING_ACCOUNTS_RECEIVABLE,
                        name: 'Accounts Receivable',
                        type: 'Asset',
                    },
                    feeType: newFeeType,
                    person: {
                        id: person.id,
                        name: getPersonSimpleName(person.name),
                    },
                },
            ];

            if (license) {
                newLineItems.forEach(
                    (item) =>
                        (item.license = {
                            id: license.id,
                            licenseNumber: license.licenseNumber,
                            type: license.type,
                        }),
                );
            }
            const accountsReceivableItems = newLineItems.filter(
                (item) => item.account.id === ACCOUNTING_ACCOUNTS_RECEIVABLE,
            );
            if (
                accountsReceivableItems.reduce(
                    (sum, item) => sum + (item.type === 'debit' ? item.amount : -item.amount),
                    invoiceBalance,
                ) > 0
            ) {
                addLineItems(invoice.id, newLineItems);
                setIsEditable(true);
                setLineItems([...lineItems, ...newLineItems]);
                const calculatedTotal = sanitizeAmount(
                    accountsReceivableItems.reduce(
                        (sum, item) => sum + (item.type === 'debit' ? item.amount : -item.amount),
                        invoiceTotal,
                    ),
                );
                setInvoiceTotal(calculatedTotal);
                setInvoiceBalance(calculatedTotal - amountReceived);
                setLineItem({
                    type: 'debit',
                    date: '',
                    item: lineItem.item,
                    amount: '',
                    description: '',
                    action: 'Reduce',
                });
            } else {
                setAmountErrorMessage(true);
            }
        } else {
            setErrorMessage(true);
        }
    };

    return (
        <Paper className={classes.paper}>
            <Grid container spacing={8}>
                <Grid item xs={8}>
                    <Typography variant="h5">{invoice.invoicing?.type.name}</Typography>
                </Grid>
                <Grid item xs={4}>
                    <Typography className={classes.invoiceDate}>{moment(invoice.date).format('LL')}</Typography>
                </Grid>
                <Grid item xs={8}>
                    {isEditable && isUnpaid && (
                        <Button title={'Edit'} onClick={handleEditIcon} className={classes.modalBtn} variant="outlined">
                            <EditIcon />
                            Edit
                        </Button>
                    )}
                    {!isEditable && isUnpaid && (
                        <>
                            <Button
                                title={'Save'}
                                onClick={handleCloseIcon}
                                className={classes.modalBtn}
                                variant="outlined"
                            >
                                <CloseIcon />
                                Cancel
                            </Button>
                            <Button
                                title={'Save'}
                                onClick={handleSaveIcon}
                                className={classes.modalBtn}
                                variant="outlined"
                            >
                                <CheckIcon />
                                Save
                            </Button>
                        </>
                    )}
                    {errorMessage && (
                        <Typography color="error">
                            *Item, description (Increase/Reduce and text), and amount are required
                        </Typography>
                    )}
                    {amountErrorMessage && (
                        <Typography color="error">*Invoice balance must be greater than zero</Typography>
                    )}
                </Grid>
                <Grid item xs={6}>
                    <Typography className={classes.invoiceeName}>{invoice.invoicing?.billTo.name}</Typography>
                    <Typography>{invoice.invoicing?.billTo.address?.line1}</Typography>
                    <Typography>{invoice.invoicing?.billTo.address?.line2}</Typography>
                    <Typography>
                        {invoice.invoicing?.billTo?.address?.city} {invoice.invoicing?.billTo.address?.state}{' '}
                        {invoice.invoicing?.billTo.address?.zipcode}
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <Table>
                        <TableBody>
                            <TableRow>
                                <TableCell size="small" className={classes.label}>
                                    Invoice #
                                </TableCell>
                                <TableCell size="small">{invoice.invoicing?.invoiceNumber}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell size="small" className={classes.label}>
                                    Due Date
                                </TableCell>
                                <TableCell size="small">
                                    {invoice.invoicing?.dueDate && moment(invoice.invoicing?.dueDate).format('LL')}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </Grid>
            </Grid>
            <Table className={classes.invoiceItems}>
                <TableHead>
                    <TableRow>
                        <TableCell className={classes.label}>Service Date</TableCell>
                        <TableCell className={classes.label}>Item</TableCell>
                        <TableCell className={classes.label}>Description</TableCell>
                        <TableCell className={classes.label} align="right">
                            Qty
                        </TableCell>
                        <TableCell className={classes.label} align="right">
                            Rate
                        </TableCell>
                        <TableCell className={classes.label} align="right" width="15%">
                            Amount
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {lineItems
                        .filter((item) => item.account.id === ACCOUNTING_ACCOUNTS_RECEIVABLE)
                        .map((item) => (
                            <TableRow key={uuid()}>
                                <TableCell>{item.serviceDate && moment(item.serviceDate).format('L')}</TableCell>
                                <TableCell>{item.feeType?.name}</TableCell>
                                <TableCell>{item.description}</TableCell>
                                <TableCell align="right">{item.units}</TableCell>
                                <TableCell align="right">
                                    {item.unitPrice && displayCurrencyAmount(item.unitPrice)}
                                </TableCell>
                                <TableCell align="right">
                                    {displayCurrencyAmount(item.type === 'debit' ? item.amount : -item.amount)}
                                </TableCell>
                            </TableRow>
                        ))}
                    {!isEditable && (
                        <TableRow>
                            <TableCell>
                                <DatePicker
                                    value={lineItem.date}
                                    label={''}
                                    inputProps={{
                                        classes: {
                                            root: classes.datePickerInput,
                                        },
                                    }}
                                    handleDate={(e: string) => setLineItem({ ...lineItem, date: e })}
                                />
                            </TableCell>
                            <TableCell>
                                <Select
                                    defaultValue={lineItem.item}
                                    fullWidth
                                    value={lineItem.item}
                                    label="Select Item"
                                    onChange={(e) => setLineItem({ ...lineItem, item: e.target.value as string })}
                                    style={{ fontSize: 14 }}
                                >
                                    {invoiceItems
                                        .filter(
                                            (a, i) =>
                                                invoiceItems.findIndex(
                                                    (s) =>
                                                        a.feeType && s.feeType && a.feeType!.name === s.feeType!.name,
                                                ) === i,
                                        )
                                        .map((item) => (
                                            <MenuItem style={{ fontSize: 14 }} key={uuid()} value={item.feeType!.name}>
                                                {item.feeType!.name}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </TableCell>
                            <TableCell>
                                <TextField
                                    InputProps={{
                                        startAdornment: (
                                            <Select
                                                value={lineItem.action}
                                                defaultValue={'Reduce'}
                                                label="Edit Action"
                                                onChange={(e) => {
                                                    if (e.target.value === 'Reduce') {
                                                        setLineItem({
                                                            ...lineItem,
                                                            type: 'debit',
                                                            action: e.target.value as string,
                                                        });
                                                    }
                                                    if (e.target.value === 'Increase') {
                                                        setLineItem({
                                                            ...lineItem,
                                                            type: 'credit',
                                                            action: e.target.value as string,
                                                        });
                                                    }
                                                }}
                                                style={{
                                                    fontSize: 14,
                                                    fontWeight: 'bold',
                                                    color: theme.palette.primary.main,
                                                    background: grey[100],
                                                    width: '120px',
                                                }}
                                            >
                                                <MenuItem style={{ fontSize: 14, fontWeight: 'bold' }} value={'Reduce'}>
                                                    REDUCE
                                                </MenuItem>
                                                <MenuItem
                                                    style={{ fontSize: 14, fontWeight: 'bold' }}
                                                    value={'Increase'}
                                                >
                                                    INCREASE
                                                </MenuItem>
                                            </Select>
                                        ),
                                        classes: {
                                            adornedStart: classes.startAdornment,
                                        },
                                    }}
                                    variant="outlined"
                                    name="description"
                                    fullWidth
                                    size="small"
                                    className={classes.textField}
                                    value={lineItem.description}
                                    onChange={(e) =>
                                        setLineItem({ ...lineItem, description: e.target.value as string })
                                    }
                                />
                            </TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell>
                                <InputTextComponent
                                    id={`amount`}
                                    size="small"
                                    fontSize="13"
                                    variant="outlined"
                                    className={classes.textField}
                                    inputProps={classNames(classes.textFieldInput, classes.amountInput)}
                                    onChange={(amount: string) => {
                                        setLineItem({ ...lineItem, amount });
                                    }}
                                    value={lineItem.amount}
                                    inputType={'currency'}
                                />
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TableCell
                            size="small"
                            colSpan={5}
                            align="right"
                            className={`${classes.label} ${classes.invoiceSummary}`}
                        >
                            Total
                        </TableCell>
                        <TableCell size="small" align="right" className={classes.invoiceSummary}>
                            {displayCurrencyAmount(invoiceTotal)}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell
                            size="small"
                            colSpan={5}
                            align="right"
                            className={`${classes.label} ${classes.invoiceSummary}`}
                        >
                            Amount Received
                        </TableCell>
                        <TableCell size="small" align="right" className={classes.invoiceSummary}>
                            {displayCurrencyAmount(amountReceived)}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell
                            size="small"
                            colSpan={5}
                            align="right"
                            className={`${classes.label} ${classes.invoiceSummary}`}
                        >
                            Invoice Balance
                        </TableCell>
                        <TableCell
                            size="small"
                            align="right"
                            className={`${classes.invoiceBalance} ${classes.invoiceSummary}`}
                        >
                            {displayCurrencyAmount(invoiceBalance)}
                        </TableCell>
                    </TableRow>
                </TableFooter>
            </Table>
        </Paper>
    );
};

export default Invoice;
